import { useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * useSmartTemplates — Loads smart templates from history, loads team providers,
 * and applies templates to the form.
 * Extracted from App.js to reduce monolith.
 */
const useSmartTemplates = ({
  user, effectiveRole,
  setIsLoadingSmartTemplates, setSmartTemplates,
  setMyProviders,
  setImagingOrders, setSpecialties, setPatientStatus,
}) => {
  // Load smart templates from history analysis
  useEffect(() => {
    const loadSmartTemplates = async () => {
      if (user && effectiveRole === 'provider') {
        setIsLoadingSmartTemplates(true);
        try {
          const response = await axios.get(`${API}/history/smart-templates`, { withCredentials: true });
          setSmartTemplates(response.data);
        } catch (error) {
          console.error('Failed to load smart templates:', error);
        } finally {
          setIsLoadingSmartTemplates(false);
        }
      }
    };
    loadSmartTemplates();
  }, [user, effectiveRole, setIsLoadingSmartTemplates, setSmartTemplates]);

  // Load providers I'm a team member of
  useEffect(() => {
    const loadMyProviders = async () => {
      if (user) {
        try {
          const response = await axios.get(`${API}/team/my-providers`, { withCredentials: true });
          setMyProviders(response.data.providers || []);
        } catch (error) {
          console.error('Failed to load team providers:', error);
        }
      } else {
        setMyProviders([]);
      }
    };
    loadMyProviders();
  }, [user, setMyProviders]);

  // Apply a smart template to form fields
  const applySmartTemplate = useCallback((template) => {
    if (template.imaging && template.imaging.length > 0) {
      const newImagingOrders = template.imaging.map(img => ({
        type: img.type || '',
        bodyPart: img.body_part || ''
      }));
      setImagingOrders(newImagingOrders);
    }
    if (template.referrals && template.referrals.length > 0) {
      setSpecialties(template.referrals);
    }
    if (template.status) {
      setPatientStatus(template.status);
    }
    toast.success(`Applied "${template.name}" template`);
  }, [setImagingOrders, setSpecialties, setPatientStatus]);

  return { applySmartTemplate };
};

export default useSmartTemplates;
